import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Bell } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const getToasted = () => {
  try { return new Set(JSON.parse(localStorage.getItem("toasted_ntf") || "[]")); }
  catch { return new Set(); }
};
const saveToasted = (s) => localStorage.setItem("toasted_ntf", JSON.stringify([...s].slice(-200)));

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      const seen = getToasted();
      data
        .filter((n) => !n.read && !seen.has(n.notification_id))
        .forEach((n) => {
          toast(n.title, { description: n.message, duration: 8000 });
          seen.add(n.notification_id);
        });
      saveToasted(seen);
    } catch (err) { console.debug("notifications poll skipped:", err?.message); }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && unread > 0) {
      try {
        await api.post("/notifications/read");
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      } catch (err) { console.debug("mark-read skipped:", err?.message); }
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-secondary text-foreground/80 hover:text-foreground hover:-translate-y-0.5 transition-transform duration-200"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white"
            >
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b border-black/10">
          <p className="text-sm font-semibold">Notifications</p>
        </div>
        <div className="max-h-96 overflow-y-auto no-scrollbar">
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground text-center">You're all caught up.</p>
          ) : (
            items.slice(0, 12).map((n) => (
              <button
                key={n.notification_id}
                data-testid={`notification-${n.notification_id}`}
                onClick={() => {
                  if (n.match_id) navigate(`/room/${n.match_id}`);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-3 border-b border-black/5 hover:bg-secondary transition-colors ${n.read ? "opacity-70" : "bg-primary/5"}`}
              >
                <p className="text-sm font-semibold">{n.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground leading-snug">{n.message}</p>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
